import { ReactNode, useEffect, useState } from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'

import TenantService from 'common/services/tenant'
import { getTenantSlug } from 'common/tenant'
import { setKnownCustomDomain } from 'common/tenant/custom-host'
import {
  tenantDisplayUrl,
  tenantUrl,
  usesTenantSubdomain
} from 'common/tenant/url'

import Backdrop from 'components/ui/backdrop'

interface TenantGuardProps {
  children: ReactNode
}

/**
 * Confirma que o slug da URL corresponde a uma imobiliaria de verdade antes de
 * deixar a aplicacao renderizar.
 *
 * Sem isso, um endereco digitado errado carregaria o site inteiro e so falharia
 * requisicao a requisicao, com telas vazias e sem explicacao. Quando a URL nao
 * tem slug — o caso da imobiliaria original — nao ha o que validar e a
 * renderizacao segue direto.
 */
export default function TenantGuard({ children }: TenantGuardProps) {
  const slug = getTenantSlug()
  const [status, setStatus] = useState<'checking' | 'ok' | 'not-found'>(
    slug ? 'checking' : 'ok'
  )

  useEffect(() => {
    if (!slug) return

    let active = true
    TenantService.findBySlug(slug)
      .then((tenant) => {
        if (!active) return
        // Feature #538: o canonical aponta para o dominio proprio, se houver.
        setKnownCustomDomain(tenant?.customDomain)
        setStatus(tenant ? 'ok' : 'not-found')
      })
      .catch(() => {
        // Falha de rede nao e slug invalido — deixa a aplicacao seguir e tratar
        // o erro no seu proprio fluxo.
        if (active) setStatus('ok')
      })

    return () => {
      active = false
    }
  }, [slug])

  if (status === 'checking') {
    return <Backdrop />
  }

  if (status === 'not-found') {
    // Com subdominio o endereco errado e o host inteiro, e nao um pedaco do
    // caminho: mostrar `/vistamar` para quem digitou `vistmar.seulugar.imb.br`
    // nao ajudaria ninguem a achar o erro. E o botao de saida precisa sair do
    // host quebrado, senao recarrega a mesma tela.
    const address = usesTenantSubdomain()
      ? tenantDisplayUrl('/', slug)
      : `/${slug}`
    const homeUrl = usesTenantSubdomain() ? tenantUrl('/', null) : '/'

    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          px: 3,
          textAlign: 'center'
        }}
      >
        <Typography variant='h5' fontWeight={700}>
          Imobiliária não encontrada
        </Typography>
        <Typography color='text.secondary' sx={{ maxWidth: 420 }}>
          O endereço <strong>{address}</strong> não corresponde a nenhuma
          imobiliária cadastrada. Confira o link ou fale com quem o enviou.
        </Typography>
        <Button
          variant='contained'
          color='primary'
          href={homeUrl}
          sx={{ mt: 1, borderRadius: 2 }}
        >
          Ir para a página inicial
        </Button>
      </Box>
    )
  }

  return <>{children}</>
}
